import React, { useState, useEffect, useMemo } from "react";
import Loader from "./Loader";
import Search from "../assets/svgIcons/Search.svg";
import Dropdown from "../assets/svgIcons/PrimaryDropDown.svg";
import { useOutsideClick } from "../utils/useOutsideClick";
import SelectTransactionTable from "./SelectTransactionTable";
import {
  fetchTransactionByCode,
  fetchPickUpTransactionByCode,
} from "../apis/transaction";
import { displayToast } from "../utils/Toast";
import { formatDate } from "../utils/Formatter";
import { useErrorHandler } from "../services/ErrorHandler";
import { useSalesFormStore } from "../services/useSalesFormStore";
import "../styles/components/SelectTransaction.css";

function SelectTransaction({ onClose, transactionCode }) {
  const {
    setSelectedPickUpTransaction,
    selectedPickUpTransaction,
    setSelectedTransaction,
    selectedTransaction,
    setPickUpData,
    setSelectedPickUpTransactionItems,
    selectedPickUpTransactionItems,
  } = useSalesFormStore();
  const { handleErrorResponse } = useErrorHandler();
  const [loading, setLoading] = useState({
    transactionDataLoading: false,
    tableDataLoading: false,
  });
  const [tableData, setTableData] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [tableHeadings, setTableHeadings] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedOperation, setSelectedOperation] = useState("");
  const [isTransactionDropdownOpen, setIsTransactionDropdownOpen] =
    useState(false);
  const [localSelectedItems, setLocalSelectedItems] = useState(
    selectedPickUpTransactionItems || []
  );
  const [localSelectedPickUpTransaction, setLocalSelectedPickUpTransaction] =
    useState(selectedPickUpTransaction || {});
  const [isOperationDropdownOpen, setIsOperationDropdownOpen] = useState(false);
  const operations = ["=", "%", ">", ">=", "<", "<="];
  const isLoading = loading.transactionDataLoading || loading.tableDataLoading;

  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  });

  const operationDropdownRef = useOutsideClick(() => {
    setIsOperationDropdownOpen(false);
  });

  const transactionDropdownRef = useOutsideClick(() => {
    setIsTransactionDropdownOpen(false);
  });

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
  };

  const handleOperationSelect = (operation) => {
    setSelectedOperation(operation);
    setIsOperationDropdownOpen(false);
  };

  const applyFilter = (data, fieldName, operation, value) => {
    return data.filter((item) => {
      const fieldValue = item[fieldName];

      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      const itemValue = String(fieldValue).toLowerCase();
      const searchVal = String(value).toLowerCase();

      switch (operation) {
        case "=":
          return itemValue === searchVal;
        case "%":
          return itemValue.includes(searchVal);
        case ">":
          if (!isNaN(fieldValue) && !isNaN(value)) {
            return Number(fieldValue) > Number(value);
          }
          return itemValue > searchVal;
        case ">=":
          if (!isNaN(fieldValue) && !isNaN(value)) {
            return Number(fieldValue) >= Number(value);
          }
          return itemValue >= searchVal;
        case "<":
          if (!isNaN(fieldValue) && !isNaN(value)) {
            return Number(fieldValue) < Number(value);
          }
          return itemValue < searchVal;
        case "<=":
          if (!isNaN(fieldValue) && !isNaN(value)) {
            return Number(fieldValue) <= Number(value);
          }
          return itemValue <= searchVal;
        default:
          return true;
      }
    });
  };

  const handleSearch = () => {
    if (!selectedCategory || !selectedCategory.field_name) {
      displayToast("Category not selected", "error");
      return;
    }

    if (!selectedOperation) {
      displayToast("Operation not selected", "error");
      return;
    }

    if (!searchValue.trim()) {
      displayToast("Please enter a search value", "error");
      return;
    }

    const filteredData = applyFilter(
      originalTableData,
      selectedCategory.field_name,
      selectedOperation,
      searchValue
    );

    setTableData(filteredData);

    if (filteredData.length === 0) {
      displayToast("No results found", "info");
    } else {
      displayToast(`Found ${filteredData.length} result(s)`, "success");
    }
  };

  const handleClearFilter = () => {
    setTableData(originalTableData);
    setSearchValue("");
    setSelectedCategory("");
    setSelectedOperation("");
    displayToast("Filter cleared", "info");
  };

  const memoizedTranCode = useMemo(() => {
    return selectedTransaction?.tran_code;
  }, [selectedTransaction]);

  useEffect(() => {
    fetchTableTransactionData();
  }, []);

  useEffect(() => {
    if (memoizedTranCode) {
      fetchTableData();
    }
  }, [memoizedTranCode]);

  const fetchTableTransactionData = async () => {
    try {
      setLoading((prevState) => ({
        ...prevState,
        transactionDataLoading: true,
      }));
      const tableHeadingsData = await fetchTransactionByCode({
        transactionCode,
      });

      if (tableHeadingsData.description) {
        handleErrorResponse(tableHeadingsData.description);
        displayToast(tableHeadingsData.description, "error");
      } else {
        setTableHeadings({
          transactions:
            tableHeadingsData?.TransactionSettings?.pick_up_transactions || [],
          pickupHeadings:
            tableHeadingsData?.TransactionSettings.pick_up_head_view_fields ||
            [],
          pickupItemHeadings:
            tableHeadingsData?.TransactionSettings?.pick_up_item_view_fields ||
            [],
          rows: tableHeadingsData?.TransactionList || [],
          filters: tableHeadingsData?.TransactionSettings?.filter_data || [],
        });
        setPickUpData(
          tableHeadingsData?.TransactionSettings?.pick_up_data || []
        );
      }
    } catch (error) {
      console.error("Error fetching transaction data:", error);
      displayToast("Failed to fetch transaction data", "error");
    } finally {
      setLoading((prevState) => ({
        ...prevState,
        transactionDataLoading: false,
      }));
    }
  };

  const fetchTableData = async () => {
    try {
      setLoading((prevState) => ({
        ...prevState,
        tableDataLoading: true,
      }));
      let type = `head_data/${selectedTransaction?.tran_code}`;
      const tableDataResponse = await fetchPickUpTransactionByCode(type);
      if (tableDataResponse.description) {
        handleErrorResponse(tableDataResponse.description);
        displayToast(tableDataResponse.description, "error");
        setTableData([]);
        setOriginalTableData([]);
      } else {
        const items = tableDataResponse.items || [];
        setTableData(items);
        setOriginalTableData(items);
        setSearchValue("");
        setSelectedCategory("");
        setSelectedOperation("");
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      displayToast("Failed to fetch table data", "error");
      setTableData([]);
      setOriginalTableData([]);
    } finally {
      setLoading((prevState) => ({
        ...prevState,
        tableDataLoading: false,
      }));
    }
  };

  useEffect(() => {
    if (tableHeadings?.transactions?.length) {
      if (
        !selectedTransaction ||
        Object.keys(selectedTransaction).length === 0
      ) {
        setSelectedTransaction(tableHeadings.transactions[0]);
      }
    }
  }, [tableHeadings, selectedTransaction]);

  const filteredPickupHeadings = useMemo(() => {
    const sortedFields = tableHeadings?.pickupHeadings?.sort(
      (a, b) => a.order_no - b.order_no
    );
    return sortedFields?.filter((item) => item.is_internal !== true);
  }, [tableHeadings.pickupHeadings]);

  const filteredPickupItemHeadings = useMemo(() => {
    const sortedFields = tableHeadings?.pickupItemHeadings?.sort(
      (a, b) => a.order_no - b.order_no
    );
    return sortedFields?.filter((item) => item.is_internal !== true);
  }, [tableHeadings.pickupItemHeadings]);

  const handleMainTableRowClick = (rowData) => {
    setLocalSelectedPickUpTransaction((prev) => ({
      ...prev,
      [selectedTransaction.tran_code]: rowData,
    }));
  };

  const handleTransactionToggle = () => {
    setIsTransactionDropdownOpen((prev) => !prev);
  };

  const handleTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsTransactionDropdownOpen(false);
  };

  const validateSelection = () => {
    if (
      !localSelectedPickUpTransaction ||
      Object.keys(localSelectedPickUpTransaction).length === 0
    ) {
      displayToast("Please select a transaction");
      return false;
    }

    if (!localSelectedItems || localSelectedItems.length === 0) {
      displayToast("Please select transaction item");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateSelection()) {
      return;
    }
    setSelectedPickUpTransactionItems(localSelectedItems);
    Object.entries(localSelectedPickUpTransaction).forEach(
      ([tran_code, rowData]) => {
        setSelectedPickUpTransaction(tran_code, rowData);
      }
    );
    onClose();
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="tab-content-detail-wrapper">
          <div
            className="transaction-table-heading-container"
            ref={transactionDropdownRef}
          >
            <button
              className="select-transaction-btn"
              onClick={handleTransactionToggle}
            >
              {selectedTransaction?.tran_name || "Select Transaction"}{" "}
              <img src={Dropdown} className="trns-dd" alt="dropdown" />
            </button>
            {isTransactionDropdownOpen && (
              <div className="transaction-dropdown-options">
                {tableHeadings?.transactions?.map((transaction, index) => (
                  <div
                    key={index}
                    className={`transaction-add-item-dropdown-option ${
                      transaction?.tran_code === selectedTransaction?.tran_code
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleTransaction(transaction)}
                  >
                    {transaction.tran_name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="tab-detail-category-table">
            <div className="tab-detail-category-table-filter-container">
              <h6 className="tab-detail-category-table-filter-text">Filter</h6>
              <div className="tab-detail-category-table-buttons">
                <div className="tab-detail-category-table-filters">
                  <div
                    className="tab-detail-category-dropdown-container"
                    ref={dropdownRef}
                  >
                    <div
                      className="tab-detail-category-dropdown-button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      {selectedCategory?.display_name || "Category"}
                      <span className="dropdown-arrow">
                        <img src={Dropdown} alt="dropdown" />
                      </span>
                    </div>

                    {isDropdownOpen && (
                      <div className="tab-detail-category-dropdown-options">
                        {filteredPickupHeadings?.length > 0 ? (
                          filteredPickupHeadings.map((heading, index) => (
                            <div
                              key={index}
                              className="add-item-dropdown-option"
                              onClick={() => handleCategorySelect(heading)}
                            >
                              {heading.display_name ||
                                heading.title ||
                                heading.field_name}
                            </div>
                          ))
                        ) : (
                          <div
                            className="add-item-dropdown-option"
                            style={{ color: "#999" }}
                          >
                            No categories available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="tab-detail-category-dropdown-container"
                    ref={operationDropdownRef}
                  >
                    <div
                      className="tab-detail-category-dropdown-button"
                      onClick={() =>
                        setIsOperationDropdownOpen(!isOperationDropdownOpen)
                      }
                    >
                      {selectedOperation || "Operation"}
                      <span className="dropdown-arrow">
                        <img src={Dropdown} alt="dropdown" />
                      </span>
                    </div>

                    {isOperationDropdownOpen && (
                      <div className="tab-detail-category-dropdown-options">
                        {operations.map((operation, index) => (
                          <div
                            key={index}
                            className="add-item-dropdown-option"
                            onClick={() => handleOperationSelect(operation)}
                          >
                            {operation}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="tab-detail-category-table-search-container">
                    <input
                      type="text"
                      placeholder="Search value"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearch();
                        }
                      }}
                      className="tab-detail-category-table-filter-search"
                    />
                    <img
                      src={Search}
                      alt="search"
                      className="tab-detail-category-table-filter-searchIcon"
                      onClick={handleSearch}
                      style={{ cursor: "pointer" }}
                    />
                  </div>

                  {(selectedCategory || selectedOperation || searchValue) && (
                    <button
                      onClick={handleClearFilter}
                      className="clear-filter-btn"
                      style={{
                        padding: "8px 16px",
                        marginLeft: "10px",
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
            </div>

            {tableData.length !== originalTableData.length &&
              originalTableData.length > 0 && (
                <div
                  style={{
                    padding: "10px",
                    backgroundColor: "#e3f2fd",
                    borderRadius: "4px",
                    margin: "10px 0",
                    fontSize: "14px",
                  }}
                >
                  Showing {tableData.length} of {originalTableData.length}{" "}
                  records
                </div>
              )}

            <div className="trasaction-table-container">
              <table>
                <thead>
                  <tr className="table-heading">
                    {filteredPickupHeadings?.map((heading, index) => (
                      <th key={index} className="table-head">
                        {heading.display_name.length > 20 ? (
                          <>
                            {heading.display_name
                              .split(" ")
                              .reduce((chunks, word) => {
                                const lastChunk = chunks[chunks.length - 1];
                                if (
                                  lastChunk &&
                                  lastChunk.length + word.length + 1 <= 20
                                ) {
                                  chunks[chunks.length - 1] =
                                    lastChunk + " " + word;
                                } else {
                                  chunks.push(word);
                                }
                                return chunks;
                              }, [])
                              .map((chunk, i) => (
                                <span key={i}>
                                  {chunk}
                                  <br />
                                </span>
                              ))}
                          </>
                        ) : (
                          heading.display_name
                        )}
                      </th>
                    ))}
                  </tr>
                  <tr className="table-border">
                    <td colSpan={filteredPickupHeadings?.length}></td>
                  </tr>
                </thead>
                <tbody>
                  {tableData && tableData.length > 0 ? (
                    tableData.map((item, rowIndex) => {
                      const selectedTrasactionCode =
                        localSelectedPickUpTransaction[
                          selectedTransaction?.tran_code
                        ] &&
                        localSelectedPickUpTransaction[
                          selectedTransaction.tran_code
                        ].tran_id;
                      return (
                        <React.Fragment key={rowIndex}>
                          <tr
                            onClick={() => handleMainTableRowClick(item)}
                            className={
                              localSelectedPickUpTransaction &&
                              selectedTrasactionCode === item.tran_id
                                ? "selected-row"
                                : "hoverable-row"
                            }
                          >
                            {filteredPickupHeadings?.map(
                              (heading, colIndex) => {
                                const cellData = item[heading.field_name];
                                const formattedCellData = [
                                  "created_at",
                                  "updated_at",
                                  "date",
                                ].includes(heading.field_name)
                                  ? formatDate(cellData)
                                  : cellData;

                                return (
                                  <td
                                    key={colIndex}
                                    className="trasaction-table-data"
                                  >
                                    {heading.field_name === "tran_no" ? (
                                      formattedCellData || "N/A"
                                    ) : formattedCellData?.length > 20 ? (
                                      <>
                                        {formattedCellData
                                          .split(" ")
                                          .reduce((chunks, word) => {
                                            const lastChunk =
                                              chunks[chunks.length - 1];
                                            if (
                                              lastChunk &&
                                              lastChunk.length +
                                                word.length +
                                                1 <=
                                                20
                                            ) {
                                              chunks[chunks.length - 1] =
                                                lastChunk + " " + word;
                                            } else {
                                              chunks.push(word);
                                            }
                                            return chunks;
                                          }, [])
                                          .map((chunk, i) => (
                                            <span key={i}>
                                              {chunk}
                                              <br />
                                            </span>
                                          ))}
                                      </>
                                    ) : (
                                      formattedCellData || "N/A"
                                    )}
                                  </td>
                                );
                              }
                            )}
                          </tr>
                          <tr className="table-border">
                            <td colSpan={filteredPickupHeadings?.length}></td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={filteredPickupHeadings?.length}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {originalTableData.length > 0
                          ? "No results match your filter criteria"
                          : "No data available"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {selectedTransaction &&
              localSelectedPickUpTransaction[selectedTransaction.tran_code] && (
                <SelectTransactionTable
                  pickUpItemHeadings={filteredPickupItemHeadings}
                  selectedMainTableRow={
                    localSelectedPickUpTransaction[
                      selectedTransaction.tran_code
                    ]
                  }
                  setLocalSelectedItems={setLocalSelectedItems}
                />
              )}
          </div>
          <div className="select-transaction-detail-container">
            <div>
              <button className="next-btn" onClick={handleSubmit}>
                NEXT
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SelectTransaction;
